import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { instructorStudentsAPI } from '../../api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Users, 
  Search, 
  Activity, 
  TrendingUp,
  Eye,
  MessageSquare,
  BarChart3,
  Clock,
  AlertTriangle,
  CheckCircle2,
  Play,
  User,
  FileText,
  ChevronRight
} from 'lucide-react';

// Metric Card Component (moved outside to prevent re-renders)
const MetricCard = ({ icon: Icon, label, value, subValue, colorClass }) => (
  <Card>
    <CardContent className="pt-6">
      <div className="flex items-center gap-4">
        <div className={`p-3 rounded-lg ${colorClass?.bg || 'bg-blue-100'}`}>
          <Icon className={`w-6 h-6 ${colorClass?.text || 'text-blue-600'}`} />
        </div>
        <div>
          <p className="text-2xl font-bold">{value}</p>
          <p className="text-sm text-gray-500">{label}</p>
          {subValue && <p className="text-xs text-gray-400">{subValue}</p>}
        </div>
      </div>
    </CardContent>
  </Card>
);

export default function MyStudentsPage() {
  const { user } = useAuth();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [metrics, setMetrics] = useState(null);
  
  // Detail modal
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentDetail, setStudentDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const loadStudents = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (searchTerm) params.search = searchTerm;
      
      const response = await instructorStudentsAPI.getMyStudents(params);
      setStudents(response.data.students);
    } catch (error) {
      toast.error('Error al cargar estudiantes');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [searchTerm]);

  const loadMetrics = useCallback(async () => {
    try {
      const response = await instructorStudentsAPI.getDashboardMetrics();
      setMetrics(response.data);
    } catch (error) {
      console.error('Error loading metrics:', error);
    }
  }, []);

  useEffect(() => {
    loadStudents();
    loadMetrics();
  }, [loadStudents, loadMetrics]);

  const openStudentDetail = async (student) => {
    setSelectedStudent(student);
    setShowDetailModal(true);
    setLoadingDetail(true);
    
    try {
      const response = await instructorStudentsAPI.getStudentDetail(student.id);
      setStudentDetail(response.data);
    } catch (error) {
      toast.error('Error al cargar detalles');
    } finally {
      setLoadingDetail(false);
    }
  };

  const getPerformanceBadge = (score) => {
    if (score >= 80) return <Badge className="bg-green-500">Excelente</Badge>;
    if (score >= 60) return <Badge className="bg-blue-500">Bueno</Badge>;
    if (score >= 40) return <Badge className="bg-yellow-500">Regular</Badge>;
    if (score > 0) return <Badge className="bg-red-500">Bajo</Badge>;
    return <Badge variant="secondary">Sin evaluar</Badge>;
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Users className="w-7 h-7 text-blue-600" />
            Mis Estudiantes
          </h1>
          <p className="text-gray-600 mt-1">
            Monitorea el progreso y rendimiento de tus estudiantes
          </p>
        </div>
      </div>

      {/* Metrics */}
      {metrics && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard
            icon={Users}
            label="Total Estudiantes"
            value={metrics.total_estudiantes}
            colorClass={{ bg: 'bg-blue-100', text: 'text-blue-600' }}
          />
          <MetricCard
            icon={Activity}
            label="Sesiones Activas"
            value={metrics.simulaciones_activas_ahora}
            subValue="Ahora mismo"
            colorClass={{ bg: 'bg-green-100', text: 'text-green-600' }}
          />
          <MetricCard
            icon={TrendingUp}
            label="Promedio General"
            value={`${metrics.promedio_puntaje_estudiantes}%`}
            colorClass={{ bg: 'bg-purple-100', text: 'text-purple-600' }}
          />
          <MetricCard
            icon={AlertTriangle}
            label="Necesitan Atención"
            value={metrics.estudiantes_con_alerta}
            subValue="Bajo rendimiento"
            colorClass={{ bg: 'bg-orange-100', text: 'text-orange-600' }}
          />
        </div>
      )}

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Buscar estudiante por nombre o email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Students List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando estudiantes...</p>
        </div>
      ) : students.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Users className="w-16 h-16 mx-auto text-gray-300" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">No hay estudiantes</h3>
            <p className="mt-2 text-gray-500">
              {searchTerm 
                ? 'No se encontraron estudiantes con ese criterio'
                : 'Aún no tienes estudiantes asignados'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {students.map((student) => (
            <Card 
              key={student.id} 
              className="hover:shadow-lg transition-shadow cursor-pointer"
              onClick={() => openStudentDetail(student)}
            >
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold text-lg">
                    {student.nombre?.charAt(0)}{student.apellido?.charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 truncate">
                      {student.nombre} {student.apellido}
                    </h3>
                    <p className="text-sm text-gray-500 truncate">{student.email}</p>
                  </div>
                </div>
                
                <div className="mt-4 grid grid-cols-2 gap-2 text-sm">
                  <div className="flex items-center gap-2 text-gray-600">
                    <FileText className="w-4 h-4" />
                    <span>{student.simulaciones_completadas || 0} simulaciones</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600">
                    <BarChart3 className="w-4 h-4" />
                    <span>Prom: {student.promedio_puntaje || 0}%</span>
                  </div>
                </div>
                
                <div className="mt-4 flex items-center justify-between">
                  {getPerformanceBadge(student.promedio_puntaje || 0)}
                  
                  {student.sesion_activa ? (
                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 animate-pulse">
                      <Play className="w-3 h-3 mr-1" />
                      En simulación
                    </Badge>
                  ) : (
                    <span className="text-xs text-gray-400">Inactivo</span>
                  )}
                </div>
                
                <div className="mt-4 pt-4 border-t flex justify-between items-center">
                  <span className="text-xs text-gray-400">Ver detalles</span>
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Student Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="sm:max-w-[700px] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold">
                {selectedStudent?.nombre?.charAt(0)}{selectedStudent?.apellido?.charAt(0)}
              </div>
              <div>
                <span>{selectedStudent?.nombre} {selectedStudent?.apellido}</span>
                <p className="text-sm font-normal text-gray-500">{selectedStudent?.email}</p>
              </div>
            </DialogTitle>
          </DialogHeader>
          
          {loadingDetail ? (
            <div className="py-12 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            </div>
          ) : studentDetail ? (
            <div className="space-y-6 py-4">
              {/* Metrics */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg text-center">
                  <p className="text-2xl font-bold text-blue-700">
                    {studentDetail.metrics.total_simulaciones}
                  </p>
                  <p className="text-xs text-blue-600">Total Simulaciones</p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg text-center">
                  <p className="text-2xl font-bold text-green-700">
                    {studentDetail.metrics.simulaciones_completadas}
                  </p>
                  <p className="text-xs text-green-600">Completadas</p>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg text-center">
                  <p className="text-2xl font-bold text-purple-700">
                    {studentDetail.metrics.promedio_puntaje}%
                  </p>
                  <p className="text-xs text-purple-600">Promedio</p>
                </div>
                <div className="bg-orange-50 p-4 rounded-lg text-center">
                  <p className="text-2xl font-bold text-orange-700">
                    {studentDetail.metrics.mejor_puntaje}%
                  </p>
                  <p className="text-xs text-orange-600">Mejor Puntaje</p>
                </div>
              </div>

              {/* Recent Sessions */}
              <div>
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Sesiones Recientes
                </h4>
                {studentDetail.recent_sessions?.length > 0 ? (
                  <div className="space-y-2 max-h-[200px] overflow-y-auto">
                    {studentDetail.recent_sessions.slice(0, 5).map((session) => (
                      <div
                        key={session.id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                      >
                        <div>
                          <p className="font-medium text-sm">{session.scenario_nombre || 'Escenario'}</p>
                          <p className="text-xs text-gray-500">
                            {new Date(session.created_at).toLocaleDateString('es-CL', {
                              day: '2-digit',
                              month: 'short',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                        <Badge variant={session.status === 'finalizada' ? 'default' : 'secondary'}>
                          {session.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm py-4 text-center">
                    No hay sesiones recientes
                  </p>
                )}
              </div>

              {/* Recent Debriefings */}
              <div>
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <BarChart3 className="w-4 h-4" />
                  Evaluaciones Recientes
                </h4>
                {studentDetail.recent_debriefings?.length > 0 ? (
                  <div className="space-y-2 max-h-[200px] overflow-y-auto">
                    {studentDetail.recent_debriefings.slice(0, 5).map((debriefing) => (
                      <div
                        key={debriefing.id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                      >
                        <div>
                          <p className="font-medium text-sm">
                            Puntaje: {debriefing.analysis?.puntuacion_total || 0}%
                          </p>
                          <p className="text-xs text-gray-500">
                            {new Date(debriefing.created_at).toLocaleDateString('es-CL')}
                          </p>
                        </div>
                        {getPerformanceBadge(debriefing.analysis?.puntuacion_total || 0)}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm py-4 text-center">
                    No hay evaluaciones recientes
                  </p>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t">
                <Button className="flex-1 gap-2" disabled>
                  <Eye className="w-4 h-4" />
                  Supervisar en Vivo
                </Button>
                <Button variant="outline" className="flex-1 gap-2" disabled>
                  <MessageSquare className="w-4 h-4" />
                  Enviar Mensaje
                </Button>
              </div>
              <p className="text-xs text-center text-gray-400">
                La supervisión en vivo estará disponible próximamente
              </p>
            </div>
          ) : (
            <p className="text-center text-gray-500 py-8">
              No se pudo cargar la información
            </p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
